import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProductContext } from "@/contexts/ProductContext";
import { cn } from "@/lib/utils";
import loadBoardAd from "@/assets/lanerunner-load-board-ad.png.asset.json";
import { HubLoadBoardAdOverlay } from "@/components/social/hubAdOverlays";

/**
 * A single house ad in the rotating slot.
 *
 * To add another ad later: upload the image asset, import its pointer JSON and
 * append one entry to LOAD_BOARD_SLOT_ADS below. Nothing else changes — the
 * slot rotates through every entry automatically (and shows a single ad with no
 * rotation when there is only one).
 */
export interface HubAd {
  /** Stable key. */
  id: string;
  /** Image URL (CDN asset pointer .url). */
  imageUrl: string;
  /** Alt text — required for a11y. */
  imageAlt: string;
  /**
   * Where the ad goes on click.
   * - `internal`: in-app route (optionally switching product context first).
   * - `external`: opens in a new tab.
   */
  target:
    | { kind: "internal"; route: string; product?: "lanerunner" | "lr-direct" }
    | { kind: "external"; href: string };
  /** Optional crisp HTML text overlay drawn over the image. */
  overlay?: React.ReactNode;
}

/** Ads shown in the LR Hub rail ad slot (rotates when more than one). */
export const LOAD_BOARD_SLOT_ADS: HubAd[] = [
  {
    id: "load-board-live",
    imageUrl: loadBoardAd.url,
    imageAlt: "Post to the LaneRunner Load Board — fast, powerful, growing daily.",
    // Load board = LaneRunner product context → Search Loads.
    target: { kind: "internal", route: "/search", product: "lanerunner" },
    overlay: <HubLoadBoardAdOverlay />,
  },
];

export interface HubAdSlotProps {
  ads?: HubAd[];
  /** Rotation interval in ms (ignored when there is only one ad). */
  intervalMs?: number;
  className?: string;
}

/**
 * Rotating house-ad slot for the LR Hub rail. Renders the ad image full-bleed
 * inside the standard Hub card shell (object-contain, natural aspect ratio) so
 * it never distorts or breaks the column.
 */
export const HubAdSlot: React.FC<HubAdSlotProps> = ({
  ads = LOAD_BOARD_SLOT_ADS,
  intervalMs = 12000,
  className,
}) => {
  const navigate = useNavigate();
  const { setContext } = useProductContext();
  const [index, setIndex] = useState(0);

  const list = useMemo(() => ads.filter(Boolean), [ads]);

  useEffect(() => {
    if (list.length < 2) return;
    const t = window.setInterval(
      () => setIndex((i) => (i + 1) % list.length),
      intervalMs,
    );
    return () => window.clearInterval(t);
  }, [list.length, intervalMs]);

  if (list.length === 0) return null;

  const ad = list[index % list.length];

  const shell =
    "group block overflow-hidden rounded-xl border border-border bg-card transition-opacity hover:opacity-95";
  const shadow = { boxShadow: "var(--shadow-card)" } as React.CSSProperties;
  const img = (
    <span className="relative block">
      <img
        src={ad.imageUrl}
        alt={ad.imageAlt}
        className="block h-auto w-full object-contain"
        loading="lazy"
      />
      {ad.overlay}
    </span>
  );

  if (ad.target.kind === "external") {
    return (
      <a
        href={ad.target.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ad.imageAlt}
        className={cn(shell, className)}
        style={shadow}
      >
        {img}
      </a>
    );
  }

  const { route, product } = ad.target;

  return (
    <button
      type="button"
      aria-label={ad.imageAlt}
      onClick={() => {
        // Mirrors ProductContextSwitcher: set product context, then navigate.
        if (product) setContext(product);
        navigate(route);
      }}
      className={cn(shell, "w-full text-left", className)}
      style={shadow}
    >
      {img}
    </button>
  );
};

export default HubAdSlot;
